import React from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';
import FormatBoldOutlinedIcon from '@mui/icons-material/FormatBoldOutlined';
import FormatItalicOutlinedIcon from '@mui/icons-material/FormatItalicOutlined';
import FormatUnderlinedOutlinedIcon from '@mui/icons-material/FormatUnderlinedOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import LooksOneOutlinedIcon from '@mui/icons-material/LooksOneOutlined';
import LooksTwoOutlinedIcon from '@mui/icons-material/LooksTwoOutlined';
import Looks3OutlinedIcon from '@mui/icons-material/Looks3Outlined';
import FormatAlignLeftOutlinedIcon from '@mui/icons-material/FormatAlignLeftOutlined';
import FormatAlignCenterOutlinedIcon from '@mui/icons-material/FormatAlignCenterOutlined';
import FormatAlignRightOutlinedIcon from '@mui/icons-material/FormatAlignRightOutlined';
import FormatColorTextOutlinedIcon from '@mui/icons-material/FormatColorTextOutlined';
import FormatColorFillOutlinedIcon from '@mui/icons-material/FormatColorFillOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import UnsplashImageLibraryDialog, {
  type UnsplashImageSelection,
} from './UnsplashImageLibraryDialog';
import { DOCUMENT_ZOOM_PRESETS, type ZoomSetting } from './TiptapDocumentCanvas';

import { ToolbarButton, ToolbarGroup } from '@/shared/components/ToolbarPrimitives';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import ToolbarColorPicker from '@/shared/components/ToolbarColorPicker';
import { isPositionInsideTableCell } from '@/shared/utils/documentImagePlaceholders';
import { getLiveEditor } from '@/shared/utils/tiptapEditor';

interface DocumentToolbarProps {
  editor: Editor | null;
  saving: boolean;
  dirty: boolean;
  canEdit?: boolean;
  docxExporting?: boolean;
  googleExporting?: boolean;
  pendingImageCount?: number;
  zoomSetting: ZoomSetting;
  onZoomChange: (next: ZoomSetting) => void;
  onPrintNow?: () => void;
  onDocxExport?: () => Promise<void> | void;
  onGoogleExport?: () => Promise<void> | void;
  onPendingImagesRefresh?: () => void;
  onPendingImagesAction?: () => void;
  /** Pass to show the Upload tab in the image library dialog. */
  uploadContext?: { userId: string; resourceId: string };
}

type HeadingValue = 'paragraph' | 'h1' | 'h2' | 'h3';
type HorizontalAlignment = 'left' | 'center' | 'right';

const FONT_FAMILIES = [
  'Arial, sans-serif',
  'Helvetica, sans-serif',
  'Times New Roman, serif',
  'Georgia, serif',
  'Courier New, monospace',
  'Verdana, sans-serif',
  'Trebuchet MS, sans-serif',
  'Palatino Linotype, serif',
];

interface ToolbarEditorState {
  headingValue: HeadingValue;
  canUndo: boolean;
  canRedo: boolean;
  isBoldActive: boolean;
  isItalicActive: boolean;
  isUnderlineActive: boolean;
  isAlignLeftActive: boolean;
  isAlignCenterActive: boolean;
  isAlignRightActive: boolean;
  isBulletListActive: boolean;
  isOrderedListActive: boolean;
  isTaskListActive: boolean;
  isLinkActive: boolean;
  isTableActive: boolean;
  canMergeCells: boolean;
  canSplitCell: boolean;
  textColor: string;
  highlightColor: string;
  fontFamily: string;
  fontSize: string;
}

const DEFAULT_TOOLBAR_STATE: ToolbarEditorState = {
  headingValue: 'paragraph',
  canUndo: false,
  canRedo: false,
  isBoldActive: false,
  isItalicActive: false,
  isUnderlineActive: false,
  isAlignLeftActive: false,
  isAlignCenterActive: false,
  isAlignRightActive: false,
  isBulletListActive: false,
  isOrderedListActive: false,
  isTaskListActive: false,
  isLinkActive: false,
  isTableActive: false,
  canMergeCells: false,
  canSplitCell: false,
  textColor: '#000000',
  highlightColor: 'transparent',
  fontFamily: 'Arial, sans-serif',
  fontSize: '',
};

function readImageAlignment(editor: Editor): HorizontalAlignment | null {
  if (!editor.isActive('image')) {
    return null;
  }

  const alignment = editor.getAttributes('image').alignment;

  return alignment === 'left' || alignment === 'right' || alignment === 'center'
    ? alignment
    : 'center';
}

const DocumentToolbar: React.FC<DocumentToolbarProps> = ({
  editor,
  saving,
  dirty,
  canEdit = true,
  docxExporting = false,
  googleExporting = false,
  pendingImageCount = 0,
  zoomSetting,
  onZoomChange,
  onPrintNow,
  onDocxExport,
  onGoogleExport,
  onPendingImagesRefresh,
  onPendingImagesAction,
  uploadContext,
}) => {
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [imageOpen, setImageOpen] = React.useState(false);
  const [unsplashOpen, setUnsplashOpen] = React.useState(false);
  const [unsplashInitialSearchTerm, setUnsplashInitialSearchTerm] = React.useState('');
  const [imageAnchorEl, setImageAnchorEl] = React.useState<null | HTMLElement>(null);
  const [tableAnchorEl, setTableAnchorEl] = React.useState<null | HTMLElement>(null);
  const [linkValue, setLinkValue] = React.useState('');
  const [imageValue, setImageValue] = React.useState('');
  const [fontSizeInput, setFontSizeInput] = React.useState<string>('');
  const [jsonDialogOpen, setJsonDialogOpen] = React.useState(false);
  const [jsonText, setJsonText] = React.useState('');
  const [jsonCopyStatus, setJsonCopyStatus] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const fontSizeFocusedRef = React.useRef(false);
  const liveEditor = getLiveEditor(editor);
  const showDevJsonTools = import.meta.env.DEV && isFeatureEnabled('scoperAiDevDialog');

  const runEditorCommand = React.useCallback(
    (command: (instance: Editor) => void) => {
      const instance = getLiveEditor(editor);
      if (!instance) return;
      command(instance);
    },
    [editor]
  );

  const readSelectedImagePlaceholder = React.useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return null;

    const selection = instance.state.selection as typeof instance.state.selection & {
      node?: {
        type?: {
          name?: string;
        };
        attrs?: Record<string, unknown>;
      };
    };

    if (selection.node?.type?.name !== 'imagePlaceholder') {
      return null;
    }

    const attrs = selection.node.attrs ?? {};
    const id = typeof attrs.id === 'string' ? attrs.id.trim() : '';
    const imageQuery = typeof attrs.imageQuery === 'string' ? attrs.imageQuery.trim() : '';

    return {
      from: selection.from,
      to: selection.to,
      id,
      imageQuery,
      insideTableCell: isPositionInsideTableCell(instance.state.doc, selection.from),
    };
  }, [editor]);

  const replaceSelectedPlaceholder = React.useCallback(
    (type: 'image' | 'imagePlaceholder', attrs: Record<string, unknown>) => {
      const instance = getLiveEditor(editor);
      if (!instance) return false;
      const selectedPlaceholder = readSelectedImagePlaceholder();
      if (!selectedPlaceholder) return false;

      const nextAttrs = {
        ...attrs,
        ...(type === 'image' && selectedPlaceholder.insideTableCell && attrs.width == null
          ? { width: '100%' }
          : {}),
        id: selectedPlaceholder.id || crypto.randomUUID(),
      };

      instance
        .chain()
        .focus()
        .insertContentAt(
          { from: selectedPlaceholder.from, to: selectedPlaceholder.to },
          { type, attrs: nextAttrs }
        )
        .run();

      return true;
    },
    [editor, readSelectedImagePlaceholder]
  );

  const toolbarState =
    useEditorState({
      editor: liveEditor,
      selector: ({ editor: instance }): ToolbarEditorState => {
        if (!instance || instance.isDestroyed) return DEFAULT_TOOLBAR_STATE;

        const headingValue: HeadingValue = instance.isActive('heading', { level: 1 })
          ? 'h1'
          : instance.isActive('heading', { level: 2 })
            ? 'h2'
            : instance.isActive('heading', { level: 3 })
              ? 'h3'
              : 'paragraph';
        const imageAlignment = readImageAlignment(instance);

        return {
          headingValue,
          canUndo: instance.can().chain().focus().undo().run(),
          canRedo: instance.can().chain().focus().redo().run(),
          isBoldActive: instance.isActive('bold'),
          isItalicActive: instance.isActive('italic'),
          isUnderlineActive: instance.isActive('underline'),
          isAlignLeftActive: imageAlignment
            ? imageAlignment === 'left'
            : instance.isActive({ textAlign: 'left' }),
          isAlignCenterActive: imageAlignment
            ? imageAlignment === 'center'
            : instance.isActive({ textAlign: 'center' }),
          isAlignRightActive: imageAlignment
            ? imageAlignment === 'right'
            : instance.isActive({ textAlign: 'right' }),
          isBulletListActive: instance.isActive('bulletList'),
          isOrderedListActive: instance.isActive('orderedList'),
          isTaskListActive: instance.isActive('taskList'),
          isLinkActive: instance.isActive('link'),
          isTableActive: instance.isActive('table'),
          canMergeCells: instance.can().chain().focus().mergeCells().run(),
          canSplitCell: instance.can().chain().focus().splitCell().run(),
          textColor: (instance.getAttributes('textStyle').color as string) || '#000000',
          highlightColor: (instance.getAttributes('highlight').color as string) || 'transparent',
          fontFamily:
            (instance.getAttributes('textStyle').fontFamily as string) || 'Arial, sans-serif',
          fontSize: (instance.getAttributes('textStyle').fontSize as string) || '',
        };
      },
    }) ?? DEFAULT_TOOLBAR_STATE;

  const applyAlignment = React.useCallback(
    (alignment: HorizontalAlignment) => {
      const instance = getLiveEditor(editor);
      if (!instance) return;

      if (instance.isActive('image')) {
        instance.chain().focus().updateAttributes('image', { alignment }).run();
        return;
      }

      instance.chain().focus().setTextAlign(alignment).run();
    },
    [editor]
  );

  const openLinkDialog = (): void => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const previousUrl = instance.getAttributes('link').href as string | undefined;
    setLinkValue(previousUrl ?? 'https://');
    setLinkOpen(true);
  };

  const applyLink = (): void => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const trimmed = linkValue.trim();
    if (!trimmed) {
      instance.chain().focus().unsetLink().run();
      setLinkOpen(false);
      return;
    }
    instance.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
    setLinkOpen(false);
  };

  const openImageDialog = (): void => {
    setImageValue('https://');
    setImageOpen(true);
  };

  const openImageMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setImageAnchorEl(event.currentTarget);
  };

  const closeImageMenu = (): void => {
    setImageAnchorEl(null);
  };

  const runTableCommand = React.useCallback(
    (command: (instance: Editor) => void) => {
      runEditorCommand(command);
      setTableAnchorEl(null);
    },
    [runEditorCommand]
  );

  const openUnsplashDialog = (): void => {
    const instance = getLiveEditor(editor);
    const selectedPlaceholder = readSelectedImagePlaceholder();
    if (selectedPlaceholder) {
      setUnsplashInitialSearchTerm(selectedPlaceholder.imageQuery.slice(0, 80));
    } else if (instance) {
      const { from, to } = instance.state.selection;
      const selection = instance.state.doc.textBetween(from, to, ' ').trim();
      setUnsplashInitialSearchTerm(selection.slice(0, 80));
    } else {
      setUnsplashInitialSearchTerm('');
    }
    setUnsplashOpen(true);
  };

  const applyImage = (): void => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const trimmed = imageValue.trim();
    if (!trimmed) return;
    const replacedPlaceholder = replaceSelectedPlaceholder('image', { src: trimmed });
    if (!replacedPlaceholder) {
      instance.chain().focus().setImage({ src: trimmed }).run();
    } else {
      onPendingImagesRefresh?.();
    }
    setImageOpen(false);
  };

  const applyUnsplashImage = (selection: UnsplashImageSelection): void => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const attrs: Record<string, unknown> = {
      src: selection.src,
      alt: selection.alt,
      title: selection.title,
    };
    if (selection.storageBucket) attrs.storageBucket = selection.storageBucket;
    if (selection.storagePath) attrs.storagePath = selection.storagePath;

    if (replaceSelectedPlaceholder('image', attrs)) {
      onPendingImagesRefresh?.();
      return;
    }

    instance
      .chain()
      .focus()
      .setImage(attrs as { src: string; alt?: string; title?: string })
      .run();
  };

  const handleHeadingChange = (event: SelectChangeEvent<HeadingValue>) => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const value = event.target.value as HeadingValue;

    if (value === 'paragraph') {
      instance.chain().focus().setParagraph().run();
      return;
    }

    const headingLevel = value === 'h1' ? 1 : value === 'h2' ? 2 : 3;
    instance
      .chain()
      .focus()
      .setHeading({ level: headingLevel })
      .command(({ tr }) => {
        const { $from, $to } = tr.selection;
        tr.setSelection(TextSelection.create(tr.doc, $from.start(), $to.end()));
        return true;
      })
      .setBold()
      .run();
  };

  const tableMenuOpen = Boolean(tableAnchorEl);
  const imageMenuOpen = Boolean(imageAnchorEl);
  const pendingImagesLabel =
    pendingImageCount === 1
      ? 'Review 1 pending image'
      : `Review ${pendingImageCount} pending images`;
  const exportDocxTitle = docxExporting ? 'Exporting DOCX...' : 'Export DOCX';
  const exportGoogleTitle = googleExporting
    ? 'Exporting to Google Docs...'
    : 'Export to Google Docs';
  const currentFontSize = parseInt(toolbarState.fontSize, 10) || 11;

  React.useEffect(() => {
    if (!fontSizeFocusedRef.current) {
      setFontSizeInput(String(currentFontSize));
    }
  }, [currentFontSize]);

  const commitFontSize = React.useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const n = parseInt(fontSizeInput, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 200) {
      instance.chain().focus().setFontSize(`${n}pt`).run();
      setFontSizeInput(String(n));
    } else {
      setFontSizeInput(String(currentFontSize));
    }
  }, [editor, fontSizeInput, currentFontSize]);

  const refreshTiptapJson = React.useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return false;
    setJsonText(JSON.stringify(instance.getJSON(), null, 2));
    setJsonCopyStatus('idle');
    return true;
  }, [editor]);

  const openTiptapJsonDialog = React.useCallback(() => {
    if (refreshTiptapJson()) {
      setJsonDialogOpen(true);
    }
  }, [refreshTiptapJson]);

  const copyTiptapJson = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setJsonCopyStatus('copied');
    } catch {
      setJsonCopyStatus('failed');
    }
  }, [jsonText]);

  return (
    <>
      <Paper
        className="document-editor-toolbar"
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          border: '1px solid #dadce0',
          borderBottom: '1px solid #dadce0',
          borderRadius: '8px 8px 0 0',
          bgcolor: '#ffffff',
        }}
      >
        <Box sx={{ px: 1.25, py: 0.75 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
            <ToolbarGroup>
              <ToolbarButton
                title="Print"
                onClick={() => onPrintNow?.()}
                disabled={!liveEditor || !onPrintNow}
              >
                <PrintOutlinedIcon fontSize="small" />
              </ToolbarButton>
              {isFeatureEnabled('docxExport') && (
                <ToolbarButton
                  title={exportDocxTitle}
                  onClick={() => void onDocxExport?.()}
                  disabled={!liveEditor || !onDocxExport || docxExporting}
                >
                  <DownloadOutlinedIcon fontSize="small" />
                </ToolbarButton>
              )}
              {onGoogleExport && (
                <ToolbarButton
                  title={exportGoogleTitle}
                  onClick={() => void onGoogleExport()}
                  disabled={!liveEditor || googleExporting}
                >
                  {googleExporting ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <CloudUploadOutlinedIcon fontSize="small" />
                  )}
                </ToolbarButton>
              )}
              {showDevJsonTools && (
                <ToolbarButton
                  title="View TipTap JSON"
                  onClick={openTiptapJsonDialog}
                  disabled={!liveEditor}
                >
                  <DataObjectOutlinedIcon fontSize="small" />
                </ToolbarButton>
              )}
              {canEdit && (
                <>
                  <ToolbarButton
                    title="Undo"
                    onClick={() =>
                      runEditorCommand((instance) => instance.chain().focus().undo().run())
                    }
                    disabled={!liveEditor || !toolbarState.canUndo}
                  >
                    <UndoOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Redo"
                    onClick={() =>
                      runEditorCommand((instance) => instance.chain().focus().redo().run())
                    }
                    disabled={!liveEditor || !toolbarState.canRedo}
                  >
                    <RedoOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                </>
              )}
            </ToolbarGroup>

            {canEdit && (
              <>
                <Divider orientation="vertical" flexItem />

                <ToolbarGroup>
                  <Select
                    size="small"
                    value={toolbarState.headingValue}
                    onChange={handleHeadingChange}
                    disabled={!liveEditor}
                    sx={{
                      height: 34,
                      minWidth: 140,
                      bgcolor: '#fff',
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.5,
                      },
                    }}
                  >
                    <MenuItem value="paragraph">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <TextFieldsOutlinedIcon fontSize="small" />
                        Normal text
                      </Box>
                    </MenuItem>
                    <MenuItem value="h1">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LooksOneOutlinedIcon fontSize="small" />
                        Heading 1
                      </Box>
                    </MenuItem>
                    <MenuItem value="h2">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LooksTwoOutlinedIcon fontSize="small" />
                        Heading 2
                      </Box>
                    </MenuItem>
                    <MenuItem value="h3">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Looks3OutlinedIcon fontSize="small" />
                        Heading 3
                      </Box>
                    </MenuItem>
                  </Select>
                </ToolbarGroup>

                <Divider orientation="vertical" flexItem />

                {/* Font family */}
                <ToolbarGroup>
                  <Select
                    size="small"
                    value={toolbarState.fontFamily}
                    onChange={(e) => {
                      runEditorCommand((instance) => {
                        instance.chain().focus().setFontFamily(e.target.value).run();
                      });
                    }}
                    disabled={!liveEditor}
                    sx={{
                      height: 34,
                      minWidth: 110,
                      fontSize: 13,
                      bgcolor: '#fff',
                      '& .MuiSelect-select': { py: 0.5 },
                    }}
                  >
                    {FONT_FAMILIES.map((f) => (
                      <MenuItem key={f} value={f} sx={{ fontFamily: f, fontSize: 13 }}>
                        {f.split(',')[0]}
                      </MenuItem>
                    ))}
                  </Select>
                </ToolbarGroup>

                {/* Font size */}
                <ToolbarGroup>
                  <Tooltip title="Decrease font size">
                    <span>
                      <ToolbarButton
                        title="Decrease font size"
                        onClick={() => {
                          const smaller = Math.max(1, currentFontSize - 1);
                          runEditorCommand((instance) => {
                            instance.chain().focus().setFontSize(`${smaller}pt`).run();
                          });
                        }}
                        disabled={!liveEditor}
                      >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </ToolbarButton>
                    </span>
                  </Tooltip>
                  <InputBase
                    value={fontSizeInput}
                    onChange={(e) => setFontSizeInput(e.target.value)}
                    onFocus={(e) => {
                      fontSizeFocusedRef.current = true;
                      e.target.select();
                    }}
                    onBlur={() => {
                      fontSizeFocusedRef.current = false;
                      commitFontSize();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitFontSize();
                        (e.target as HTMLInputElement).blur();
                      } else if (e.key === 'Escape') {
                        setFontSizeInput(String(currentFontSize));
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    disabled={!liveEditor}
                    inputProps={{
                      type: 'text',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      'aria-label': 'Font size',
                      style: {
                        width: 32,
                        textAlign: 'center',
                        fontSize: 13,
                        fontFamily: 'monospace',
                        padding: '2px 0',
                        MozAppearance: 'textfield',
                      },
                    }}
                    sx={{
                      height: 34,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 0.5,
                      px: 0.5,
                      color: 'text.primary',
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        m: 0,
                      },
                    }}
                  />
                  <Tooltip title="Increase font size">
                    <span>
                      <ToolbarButton
                        title="Increase font size"
                        onClick={() => {
                          const bigger = Math.min(200, currentFontSize + 1);
                          runEditorCommand((instance) => {
                            instance.chain().focus().setFontSize(`${bigger}pt`).run();
                          });
                        }}
                        disabled={!liveEditor}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </ToolbarButton>
                    </span>
                  </Tooltip>
                </ToolbarGroup>

                <Divider orientation="vertical" flexItem />

                <ToolbarGroup>
                  <ToolbarButton
                    title="Bold"
                    onClick={() =>
                      runEditorCommand((instance) => instance.chain().focus().toggleBold().run())
                    }
                    active={toolbarState.isBoldActive}
                    disabled={!liveEditor}
                  >
                    <FormatBoldOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Italic"
                    onClick={() =>
                      runEditorCommand((instance) => instance.chain().focus().toggleItalic().run())
                    }
                    active={toolbarState.isItalicActive}
                    disabled={!liveEditor}
                  >
                    <FormatItalicOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Underline"
                    onClick={() =>
                      runEditorCommand((instance) =>
                        instance.chain().focus().toggleUnderline().run()
                      )
                    }
                    active={toolbarState.isUnderlineActive}
                    disabled={!liveEditor}
                  >
                    <FormatUnderlinedOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarColorPicker
                    value={toolbarState.textColor}
                    onChange={(color) => {
                      runEditorCommand((instance) => {
                        instance.chain().focus().setColor(color).run();
                      });
                    }}
                    label="Text color"
                    icon={<FormatColorTextOutlinedIcon fontSize="small" />}
                    disabled={!liveEditor}
                  />
                  <ToolbarColorPicker
                    value={toolbarState.highlightColor}
                    onChange={(color) => {
                      runEditorCommand((instance) => {
                        if (color === 'transparent') {
                          instance.chain().focus().unsetHighlight().run();
                        } else {
                          instance.chain().focus().setHighlight({ color }).run();
                        }
                      });
                    }}
                    label="Highlight color"
                    icon={<FormatColorFillOutlinedIcon fontSize="small" />}
                    allowTransparent
                    disabled={!liveEditor}
                  />
                </ToolbarGroup>

                <Divider orientation="vertical" flexItem />

                <ToolbarGroup>
                  <ToolbarButton
                    title="Align left"
                    onClick={() => applyAlignment('left')}
                    active={toolbarState.isAlignLeftActive}
                    disabled={!liveEditor}
                  >
                    <FormatAlignLeftOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Align center"
                    onClick={() => applyAlignment('center')}
                    active={toolbarState.isAlignCenterActive}
                    disabled={!liveEditor}
                  >
                    <FormatAlignCenterOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Align right"
                    onClick={() => applyAlignment('right')}
                    active={toolbarState.isAlignRightActive}
                    disabled={!liveEditor}
                  >
                    <FormatAlignRightOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                </ToolbarGroup>

                <Divider orientation="vertical" flexItem />

                <ToolbarGroup>
                  <ToolbarButton
                    title="Bulleted list"
                    onClick={() =>
                      runEditorCommand((instance) =>
                        instance.chain().focus().toggleBulletList().run()
                      )
                    }
                    active={toolbarState.isBulletListActive}
                    disabled={!liveEditor}
                  >
                    <FormatListBulletedOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Numbered list"
                    onClick={() =>
                      runEditorCommand((instance) =>
                        instance.chain().focus().toggleOrderedList().run()
                      )
                    }
                    active={toolbarState.isOrderedListActive}
                    disabled={!liveEditor}
                  >
                    <FormatListNumberedOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Task list"
                    onClick={() =>
                      runEditorCommand((instance) =>
                        instance.chain().focus().toggleTaskList().run()
                      )
                    }
                    active={toolbarState.isTaskListActive}
                    disabled={!liveEditor}
                  >
                    <CheckBoxOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                </ToolbarGroup>

                <Divider orientation="vertical" flexItem />

                <ToolbarGroup>
                  <ToolbarButton
                    title="Link"
                    onClick={openLinkDialog}
                    active={toolbarState.isLinkActive}
                    disabled={!liveEditor}
                  >
                    <LinkOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton title="Image" onClick={openImageMenu} disabled={!liveEditor}>
                    <ImageOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Table options"
                    onClick={(event) => setTableAnchorEl(event.currentTarget)}
                    active={toolbarState.isTableActive}
                    disabled={!liveEditor}
                  >
                    <TableChartOutlinedIcon fontSize="small" />
                  </ToolbarButton>
                </ToolbarGroup>

                {pendingImageCount > 0 && onPendingImagesAction && (
                  <>
                    <Divider orientation="vertical" flexItem />

                    <ToolbarGroup>
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        startIcon={<ImageSearchOutlinedIcon fontSize="small" />}
                        onClick={() => onPendingImagesAction()}
                        sx={{ minHeight: 34, textTransform: 'none' }}
                      >
                        {pendingImagesLabel}
                      </Button>
                    </ToolbarGroup>
                  </>
                )}
              </>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select
                size="small"
                value={zoomSetting === 'fit-width' ? 'fit-width' : String(zoomSetting)}
                onChange={(event) => {
                  const value = event.target.value;
                  onZoomChange(value === 'fit-width' ? 'fit-width' : Number(value));
                }}
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: 12,
                  color: 'text.secondary',
                  minWidth: 64,
                  '& .MuiSelect-select': { py: 0, textAlign: 'center' },
                }}
                renderValue={(v) => (v === 'fit-width' ? 'Fit' : `${v}%`)}
              >
                <MenuItem value="fit-width" sx={{ fontSize: 13 }}>
                  Fit
                </MenuItem>
                {DOCUMENT_ZOOM_PRESETS.map((p) => (
                  <MenuItem key={p} value={String(p)} sx={{ fontSize: 13 }}>
                    {p}%
                  </MenuItem>
                ))}
              </Select>
              {canEdit && (
                <Chip
                  size="small"
                  color={saving ? 'warning' : dirty ? 'info' : 'success'}
                  label={saving ? 'Saving...' : dirty ? 'Unsaved' : 'Saved'}
                />
              )}
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="URL"
            placeholder="https://example.com"
            value={linkValue}
            onChange={(event) => setLinkValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button onClick={applyLink} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>TipTap JSON</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={18}
            maxRows={28}
            margin="dense"
            value={jsonText}
            slotProps={{
              input: {
                readOnly: true,
                sx: {
                  alignItems: 'flex-start',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  lineHeight: 1.45,
                },
              },
            }}
            helperText={
              jsonCopyStatus === 'copied'
                ? 'Copied to clipboard.'
                : jsonCopyStatus === 'failed'
                  ? 'Unable to copy to clipboard.'
                  : 'Current editor document JSON from editor.getJSON().'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={refreshTiptapJson}>Refresh</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
            onClick={() => void copyTiptapJson()}
            disabled={!jsonText}
          >
            Copy JSON
          </Button>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageOpen} onClose={() => setImageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Image URL"
            placeholder="https://example.com/image.png"
            value={imageValue}
            onChange={(event) => setImageValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageOpen(false)}>Cancel</Button>
          <Button onClick={applyImage} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      <UnsplashImageLibraryDialog
        open={unsplashOpen}
        onClose={() => setUnsplashOpen(false)}
        onSelectImage={applyUnsplashImage}
        initialSearchTerm={unsplashInitialSearchTerm}
        uploadContext={uploadContext}
      />

      <Menu open={imageMenuOpen} anchorEl={imageAnchorEl} onClose={closeImageMenu}>
        <MenuItem
          onClick={() => {
            closeImageMenu();
            openUnsplashDialog();
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchOutlinedIcon fontSize="small" />
            Search image library
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeImageMenu();
            openImageDialog();
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkOutlinedIcon fontSize="small" />
            Insert by URL
          </Box>
        </MenuItem>
      </Menu>

      <Menu open={tableMenuOpen} anchorEl={tableAnchorEl} onClose={() => setTableAnchorEl(null)}>
        <InsertTableSubmenuItem
          disabled={toolbarState.isTableActive}
          onSelect={(rows, cols) => {
            runTableCommand((instance) => {
              instance.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run();
            });
          }}
        />
        {/* duplicateRow / duplicateColumn come from tiptap-table-plus only */}
        {liveEditor &&
          typeof (liveEditor.commands as Record<string, unknown>).duplicateRow === 'function' && (
            <MenuItem
              onClick={() => {
                runTableCommand((instance) => {
                  instance.chain().focus().duplicateRow().run();
                });
              }}
              disabled={!toolbarState.isTableActive}
            >
              Duplicate Row
            </MenuItem>
          )}
        {liveEditor &&
          typeof (liveEditor.commands as Record<string, unknown>).duplicateColumn ===
            'function' && (
            <MenuItem
              onClick={() => {
                runTableCommand((instance) => {
                  instance.chain().focus().duplicateColumn().run();
                });
              }}
              disabled={!toolbarState.isTableActive}
            >
              Duplicate Column
            </MenuItem>
          )}
        <Divider />
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().addColumnBefore().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Add Column Before
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().addColumnAfter().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Add Column After
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().deleteColumn().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Delete Column
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().addRowBefore().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Add Row Before
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().addRowAfter().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Add Row After
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().deleteRow().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Delete Row
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().mergeCells().run();
            });
          }}
          disabled={!toolbarState.canMergeCells}
        >
          Merge Cells
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().splitCell().run();
            });
          }}
          disabled={!toolbarState.canSplitCell}
        >
          Split Cell
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().toggleHeaderRow().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Toggle Header Row
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().toggleHeaderColumn().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Toggle Header Column
        </MenuItem>
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().toggleHeaderCell().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Toggle Header Cell
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            runTableCommand((instance) => {
              instance.chain().focus().deleteTable().run();
            });
          }}
          disabled={!toolbarState.isTableActive}
        >
          Delete Table
        </MenuItem>
      </Menu>
    </>
  );
};

const PICKER_MIN = 5;
const PICKER_MAX = 10;
const CELL_SIZE = 20;
const CELL_GAP = 2;

const InsertTableSubmenuItem: React.FC<{
  onSelect: (rows: number, cols: number) => void;
  disabled?: boolean;
}> = ({ onSelect, disabled }) => {
  const itemRef = React.useRef<HTMLLIElement>(null);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>(null);

  const openPopover = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setPopoverOpen(true);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setPopoverOpen(false), 150);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  return (
    <>
      <MenuItem
        ref={itemRef}
        onMouseEnter={openPopover}
        onMouseLeave={scheduleClose}
        disabled={disabled}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <ListItemText>Insert table</ListItemText>
        <ListItemIcon sx={{ minWidth: 'unset', ml: 2 }}>
          <ArrowRightIcon fontSize="small" />
        </ListItemIcon>
      </MenuItem>
      <Popover
        open={popoverOpen && !disabled}
        anchorEl={itemRef.current}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setPopoverOpen(false)}
        disableRestoreFocus
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: { p: 1.5, ml: 0.25 },
          },
        }}
        sx={{ pointerEvents: 'none' }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          <TableSizePicker onSelect={onSelect} />
        </Box>
      </Popover>
    </>
  );
};

const TableSizePicker: React.FC<{
  onSelect: (rows: number, cols: number) => void;
}> = ({ onSelect }) => {
  const [hover, setHover] = React.useState<{ r: number; c: number } | null>(null);
  const [gridSize, setGridSize] = React.useState({ rows: PICKER_MIN, cols: PICKER_MIN });

  const handleHover = React.useCallback((r: number, c: number) => {
    setHover({ r, c });
    setGridSize((prev) => ({
      rows: Math.max(prev.rows, Math.min(r + 2, PICKER_MAX)),
      cols: Math.max(prev.cols, Math.min(c + 2, PICKER_MAX)),
    }));
  }, []);

  const handleLeave = React.useCallback(() => {
    setHover(null);
    setGridSize({ rows: PICKER_MIN, cols: PICKER_MIN });
  }, []);

  const label = hover ? `${hover.r} × ${hover.c}` : 'Select size';

  return (
    <Box onMouseLeave={handleLeave}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridSize.cols}, ${CELL_SIZE}px)`,
          gap: `${CELL_GAP}px`,
        }}
      >
        {Array.from({ length: gridSize.rows }, (_, ri) =>
          Array.from({ length: gridSize.cols }, (_, ci) => {
            const r = ri + 1;
            const c = ci + 1;
            const highlighted = hover != null && r <= hover.r && c <= hover.c;
            return (
              <Box
                key={`${r}-${c}`}
                onMouseEnter={() => handleHover(r, c)}
                onClick={() => onSelect(r, c)}
                sx={{
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  border: '1px solid',
                  borderColor: highlighted ? '#0b57d0' : '#dadce0',
                  bgcolor: highlighted ? '#e8f0fe' : 'transparent',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  transition: 'background-color 0.05s, border-color 0.05s',
                }}
              />
            );
          })
        )}
      </Box>
      <Box
        sx={{
          textAlign: 'center',
          mt: 0.75,
          fontSize: 12,
          color: 'text.secondary',
          userSelect: 'none',
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

export default DocumentToolbar;
